import { useCallback, useEffect, useState } from 'react';

import type { NotificationType, UseDismissedNotificationsStorageReturn } from './types';

const SESSION_STORAGE_KEY = 'starterDashboardV2_dismissedNotifications';

const readFromSession = (): Set<NotificationType> => {
	if (typeof window === 'undefined') return new Set();
	try {
		const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
		if (!raw) return new Set();
		const parsed: unknown = JSON.parse(raw);
		if (Array.isArray(parsed)) {
			return new Set(parsed.filter((v): v is NotificationType => typeof v === 'string'));
		}
	} catch {
		// ignore
	}
	return new Set();
};

export function useDismissedNotificationsStorage(): UseDismissedNotificationsStorageReturn {
	const [dismissedNotifications, setDismissedNotifications] = useState<Set<NotificationType>>(() => readFromSession());

	useEffect(() => {
		if (typeof window === 'undefined') {
			return () => {};
		}

		const handler = (e: StorageEvent) => {
			if (e.key === SESSION_STORAGE_KEY) {
				setDismissedNotifications(readFromSession());
			}
		};
		window.addEventListener('storage', handler);

		// Cleanup function
		return () => {
			window.removeEventListener('storage', handler);
		};
	}, []);

	const isDismissed = useCallback(
		(type: NotificationType) => dismissedNotifications.has(type),
		[dismissedNotifications],
	);

	const dismissNotification = useCallback((type: NotificationType) => {
		setDismissedNotifications((prev) => {
			if (prev.has(type)) {
				// Even if already dismissed, persist current state to sessionStorage to keep tests & external storage in sync
				try {
					if (typeof window !== 'undefined') {
						sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify([...prev]));
					}
				} catch {
					// non-critical
				}
				return prev; // return same Set instance: no re-render
			}
			const next = new Set(prev);
			next.add(type);
			try {
				if (typeof window !== 'undefined') {
					sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify([...next]));
				}
			} catch {
				// non-critical
			}
			return next;
		});
	}, []);

	return { isDismissed, dismissNotification, dismissedNotifications };
}
